import { useRef, useState } from "react";

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

// Зум документа «щипком» двумя пальцами + панорамирование одним пальцем.
// Масштабирование идёт через CSS transform: scale — оно НЕ влияет на layout,
// поэтому превью не раздувается и не выталкивает соседние компоненты.
// Контейнер фиксированного размера с overflow-hidden обрезает увеличенный документ.
function PinchZoom({ children, minScale = 1, maxScale = 4, className = "" }) {
    const containerRef = useRef(null);
    const [scale, setScale] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const gesture = useRef({ mode: null });

    const bounds = (s) => {
        const el = containerRef.current;
        if (!el) return { x: 0, y: 0 };
        const { width, height } = el.getBoundingClientRect();
        return { x: (width * (s - 1)) / 2, y: (height * (s - 1)) / 2 };
    };

    const clampOffset = (o, s) => {
        const b = bounds(s);
        return { x: clamp(o.x, -b.x, b.x), y: clamp(o.y, -b.y, b.y) };
    };

    const distance = (t) =>
        Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

    const onTouchStart = (e) => {
        if (e.touches.length === 2) {
            gesture.current = {
                mode: "pinch",
                startDist: distance(e.touches),
                startScale: scale,
            };
        } else if (e.touches.length === 1 && scale > 1) {
            gesture.current = {
                mode: "pan",
                startOffset: offset,
                startX: e.touches[0].clientX,
                startY: e.touches[0].clientY,
            };
        }
    };

    const onTouchMove = (e) => {
        const g = gesture.current;
        if (g.mode === "pinch" && e.touches.length === 2) {
            const ratio = distance(e.touches) / g.startDist;
            const next = clamp(g.startScale * ratio, minScale, maxScale);
            setScale(next);
            setOffset((o) => clampOffset(o, next));
        } else if (g.mode === "pan" && e.touches.length === 1) {
            const dx = e.touches[0].clientX - g.startX;
            const dy = e.touches[0].clientY - g.startY;
            setOffset(
                clampOffset(
                    { x: g.startOffset.x + dx, y: g.startOffset.y + dy },
                    scale
                )
            );
        }
    };

    const onTouchEnd = (e) => {
        if (e.touches.length === 0) {
            gesture.current = { mode: null };
            if (scale <= minScale) setOffset({ x: 0, y: 0 });
        } else if (e.touches.length === 1 && scale > 1) {
            // переход с пинча на панорамирование оставшимся пальцем
            gesture.current = {
                mode: "pan",
                startOffset: offset,
                startX: e.touches[0].clientX,
                startY: e.touches[0].clientY,
            };
        }
    };

    const reset = () => {
        setScale(1);
        setOffset({ x: 0, y: 0 });
    };

    const onDoubleClick = () => {
        if (scale > 1) reset();
        else setScale(2);
    };

    return (
        <div
            ref={containerRef}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onDoubleClick={onDoubleClick}
            className={`relative overflow-hidden ${className}`}
            style={{ touchAction: "none" }}
        >
            <div
                style={{
                    transform: `translate3d(${offset.x}px, ${offset.y}px, 0) scale(${scale})`,
                    transformOrigin: "center center",
                    transition:
                        gesture.current.mode === "pinch"
                            ? "none"
                            : "transform 0.12s ease-out",
                }}
            >
                {children}
            </div>
        </div>
    );
}

export default PinchZoom;
